import { IconButton } from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import React, { Fragment, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { apiCreateContractValuation } from "../../../../../../api/valuation/apiCreateContractValuation";
import { apiDiscardValuation } from "../../../../../../api/valuation/apiDiscardValuation";
import ProgressDialog from "../../../../../../components/ProgressDialog";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { DiscardValuationsDialog } from "../../../valuation";

const StyledDiscardButton = styled(IconButton)`
  color: ${cuideoTheme.palette.common.white};
`;

export type TPrefilterDiscardCandidate = {
  id: string;
  name: string;
  /** Valuation the employee already has in the contract, if any. */
  valuationId?: string;
};

interface IPrefilterDiscardButtonProps {
  contractId: string;
  candidates: TPrefilterDiscardCandidate[];
  onDiscard: () => void;
}

/**
 * Discards the selected prefilter employees from the offer (ECO-1480).
 * Prefilter employees are not valuations, so the ones without a valuation in
 * the contract get a "Sin Valorar" one first and then every valuation is moved
 * to "Descartado" with the reason. That way they count in "Descartadas" and the
 * contract page leaves them out of the prefilter. Requests go one after the
 * other so the progress is real and a failure stops the rest.
 */
const PrefilterDiscardButton = (props: IPrefilterDiscardButtonProps) => {
  const { contractId, candidates, onDiscard } = props;

  const { formatMessage } = useIntl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [done, setDone] = useState(0);

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleConfirm = async (reason: string) => {
    setDialogOpen(false);
    setDone(0);
    setDiscarding(true);

    let numDiscarded = 0;
    let failed = false;
    for (const candidate of candidates) {
      try {
        const valuationId =
          candidate.valuationId ??
          (await apiCreateContractValuation(contractId, candidate.id));
        await apiDiscardValuation(valuationId, reason);
      } catch (e) {
        failed = true;
        break;
      }
      numDiscarded++;
      setDone((prevDone) => prevDone + 1);
    }

    setDiscarding(false);
    if (failed) {
      toast.error(
        formatMessage({
          id: "PrefilterDiscardButton.Error",
          defaultMessage: "No se han podido descartar todos los candidatos",
        })
      );
    } else {
      toast.success(
        formatMessage(
          {
            id: "PrefilterDiscardButton.Success",
            defaultMessage: "Se han descartado {num} candidatos de la oferta",
          },
          { num: numDiscarded }
        )
      );
    }
    // Also after a failure, so the ones already discarded leave the prefilter
    onDiscard();
  };

  const candidateNames = candidates
    .map((candidate) => candidate.name)
    .filter((name) => !!name);

  return (
    <Fragment>
      <StyledDiscardButton
        className="MuiCuideo-DiscardButton"
        onClick={handleClick}
        disabled={candidates.length === 0}
      >
        <DeleteOutlineIcon />
      </StyledDiscardButton>
      <DiscardValuationsDialog
        open={dialogOpen}
        candidateNames={candidateNames}
        // Every discarded employee ends up as a real valuation (ECO-1293)
        reasonRequired
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <ProgressDialog
        open={discarding}
        label={
          <FormattedMessage
            id="PrefilterDiscardButton.Discarding"
            defaultMessage="Descartando..."
          />
        }
        iteration={done}
        totalIterations={candidates.length}
      />
    </Fragment>
  );
};

export default PrefilterDiscardButton;
